import autoTable from 'jspdf-autotable';
import { formatDate, formatMoney } from 'lib/functions/utils';
import { NotRecognizedConciliation, PayIn } from 'lib/types/models';

import { BasePDFGenerator, PDFGeneratorConfig } from '../reportUtils/base';
import { getShortString } from '../reportUtils/documentUtils';
import { getProductTypeLabel } from '../reportUtils/documentUtils';
import { PayInExportConfig } from './types';

export type ProductType = 'factoring' | 'credit' | 'invoice' | 'confirming';

export interface PayInBusiness {
  id: number;
  name: string;
  identifier: string;
  startDate: string | null;
  segment: string | null;
  publicMarketCode: string | null;
}

export interface PayInStakeholder {
  id: number;
  name: string;
  identifier: string;
  startDate: string | null;
  segment: string | null;
  publicMarketCode: string | null;
}

export interface DocumentWithConciliation {
  [key: string]: unknown | number | string | null | undefined;
  // Propiedades básicas del documento
  id: number | string;
  createdAt: string;
  observation: string | null;

  // Propiedades de conciliación con PayIn
  payInId: number;
  payInConciliationId: number;
  payInDate: string;

  // Propiedades del documento
  documentId: string;
  type: string;
  typeEsp: string;
  folio: string;
  businessIdentifier: string;
  stakeholderIdentifier: string;
  businessName: string;
  stakeholderName: string;

  // Propiedades financieras
  amount: number;
  amountNet: number;
  paidAt: string;
  nulledAt: string | null;

  // Propiedades de relación con otros documentos
  fatherDocumentId: string | null;
  pendingAccountId: number | null;

  // Propiedades del producto
  productType: ProductType;
  rate: number;
  arrearRate: number;
  advance: number;
  paymentDate: string;
  extensions: unknown[];

  // Propiedades de conciliación
  amountConciliated: number;
  documentAmountDebtBefore: number;
  arrearAmountDebtBefore: number;
  documentAmountDebtAfter: number;
  arrearAmountDebtAfter: number;
  documentAmountConciliated: number;
  arrearAmountConciliated: number;

  // Relaciones opcionales
  business?: PayInBusiness;
  stakeholder?: PayInStakeholder;

  // Información adicional
  paidBy: string;
}

export interface PayInWithDetails extends PayIn {
  amountConciliated: number;
  statusTranslated: string;
  bankAccountDestinationName: string;
  bankAccountOriginName: string;
  businessDestinationName: string;
  businessDestinationIdentifier: string;
  businessOriginName: string;
  businessOriginIdentifier: string;
}

type AnyDocument = DocumentWithConciliation;

interface PayInReportConfig extends PDFGeneratorConfig {
  payIn: PayInWithDetails;
  documents: DocumentWithConciliation[];
  fncConciliations: NotRecognizedConciliation[];
  exportConfig?: PayInExportConfig;
}

function reduceDocumentsByProperty(
  documents: AnyDocument[],
  property: keyof AnyDocument | string,
  keep: 'highest' | 'lowest' | 'all' = 'all',
) {
  const groupedByFolio = documents.reduce(
    (acc, doc) => {
      if (!acc[doc.folio]) {
        acc[doc.folio] = [];
      }
      acc[doc.folio].push(doc);
      return acc;
    },
    {} as Record<string, AnyDocument[]>,
  );

  return Object.values(groupedByFolio).reduce((total, docs) => {
    if (keep === 'all') {
      return (
        total +
        docs.reduce((sum, doc) => {
          const propValue = doc[property as string];
          const numValue = typeof propValue === 'number' ? propValue : 0;
          return sum + numValue;
        }, 0)
      );
    }

    const value = docs.reduce(
      (result, doc) => {
        const propValue = doc[property as string];
        const currentValue = typeof propValue === 'number' ? propValue : 0;
        return keep === 'highest'
          ? Math.max(result, currentValue)
          : Math.min(result, currentValue);
      },
      keep === 'highest' ? -Infinity : Infinity,
    );

    return total + value;
  }, 0);
}

export class PayInReportGenerator extends BasePDFGenerator {
  private payIn: PayInWithDetails;
  private documents: DocumentWithConciliation[];
  private fncConciliations: NotRecognizedConciliation[];
  private exportConfig: PayInExportConfig;

  constructor(config: PayInReportConfig) {
    super({ ...config, orientation: 'landscape' });
    this.payIn = config.payIn;
    this.documents = config.documents;
    this.fncConciliations = config.fncConciliations;
    this.exportConfig = config.exportConfig || {};
  }

  private shouldShowField(field: keyof PayInExportConfig): boolean {
    return this.exportConfig[field] !== false;
  }

  protected addSummarySection(): number {
    const executedDocuments = this.documents.filter((document) => {
      return (
        document.payInConciliationId &&
        document.payInConciliationId > 0 &&
        document.amountConciliated &&
        document.amountConciliated > 0
      );
    });
    const totalConciliated = executedDocuments.reduce(
      (acc, document) => acc + (document.amountConciliated ?? 0),
      0,
    );
    const totalDocumentAmountConciliated = executedDocuments.reduce(
      (acc, document) => acc + (document.documentAmountConciliated ?? 0),
      0,
    );
    const totalArrearAmountConciliated = executedDocuments.reduce(
      (acc, document) => acc + (document.arrearAmountConciliated ?? 0),
      0,
    );

    const c1x = 10,
      c2x = 115,
      c3x = 210,
      valueOffset = 35,
      y0 = 30,
      line = 6;
    let y = y0;
    let col1FinalY = y0; // Para rastrear la altura final de la columna 1
    let col2FinalY = y0; // Para rastrear la altura final de la columna 2
    let col3FinalY = y0; // Para rastrear la altura final de la columna 3
    const payIn = this.payIn;

    // === COLUMNA 1: Información de origen y destino ===
    if (this.shouldShowField('showOriginInfo')) {
      this.drawKeyValue(
        'Rut origen',
        payIn.businessOriginIdentifier,
        c1x,
        y,
        valueOffset,
      );
      y += line;
      this.drawKeyValue(
        'Nombre origen',
        getShortString(payIn.businessOriginName),
        c1x,
        y,
        valueOffset,
      );
      y += line;
      this.drawKeyValue(
        'Banco origen',
        getShortString(payIn.bankAccountOriginName),
        c1x,
        y,
        valueOffset,
      );
      y += line * 1.5;
    }

    if (this.shouldShowField('showDestinationInfo')) {
      this.drawKeyValue(
        'Rut destino',
        payIn.businessDestinationIdentifier,
        c1x,
        y,
        valueOffset,
      );
      y += line;
      this.drawKeyValue(
        'Nombre destino',
        getShortString(payIn.businessDestinationName),
        c1x,
        y,
        valueOffset,
      );
      y += line;
      this.drawKeyValue(
        'Banco destino',
        getShortString(payIn.bankAccountDestinationName),
        c1x,
        y,
        valueOffset,
      );
    }
    col1FinalY = y; // Guardar la altura final de la columna 1

    // === COLUMNA 2: Resumen operación ===
    y = y0;
    if (this.shouldShowField('showOperationSummary')) {
      this.drawText(`Resumen operación ID:${payIn.id}`, c2x, y, {
        size: 12,
        bold: true,
      });
      y += line;
      this.drawKeyValue(
        'Fecha pago',
        formatDate(new Date(payIn.createdAt), 'dd-mm-yyyy'),
        c2x,
        y,
        valueOffset,
      );
      y += line;
      this.drawKeyValue('Estado', payIn.statusTranslated, c2x, y, valueOffset);
      y += line;
      this.drawKeyValue(
        'Monto pago',
        formatMoney(payIn.amount),
        c2x,
        y,
        valueOffset,
      );
      y += line;
      this.drawKeyValue(
        'Monto conciliado',
        formatMoney(payIn.amountConciliated),
        c2x,
        y,
        valueOffset,
      );
      y += line * 1.5;
    }
    col2FinalY = y;

    // === COLUMNA 3: Resumen totales ===
    y = y0;
    if (this.shouldShowField('showTotalsSummary')) {
      this.drawText('Resumen totales', c3x, y, { size: 12, bold: true });
      y += line;
      this.drawKeyValue(
        'Conciliado',
        formatMoney(totalConciliated),
        c3x,
        y,
        valueOffset,
      );
      y += line;
      this.drawKeyValue(
        'Documentos',
        formatMoney(totalDocumentAmountConciliated),
        c3x,
        y,
        valueOffset,
      );
      y += line;
      this.drawKeyValue(
        'Pendiente',
        formatMoney(payIn.amount - totalConciliated),
        c3x,
        y,
        valueOffset,
      );
      y += line;
      this.drawKeyValue(
        'Mora',
        formatMoney(totalArrearAmountConciliated),
        c3x,
        y,
        valueOffset,
      );
      y += line * 1.5;
    }
    col3FinalY = y;

    // Usar la altura real de la columna más alta con un pequeño espacio adicional
    const maxY = Math.max(col1FinalY, col2FinalY, col3FinalY) + line * 0.5;
    this.drawText(
      `* Los valores en las siguientes tablas son calculados respecto a la fecha de pago (${formatDate(new Date(payIn.createdAt), 'dd-mm-yyyy')}).`,
      c1x,
      maxY + line,
      { size: 7 },
    );
    return maxY + line * 2;
  }

  protected addContentSection(): void {
    const startY = this.addSummarySection();
    this.addDocumentsTable(startY);
    if (this.fncConciliations.length > 0) {
      this.addFncTable(
        (this.doc as any).lastAutoTable
          ? (this.doc as any).lastAutoTable.finalY + 10
          : startY + 10,
      );
    }
    // Nueva página para la tabla de Resumen conciliaciones (si está habilitada)
    if (this.shouldShowField('showConciliationsSummary')) {
      this.doc.addPage();
      const resumenTitleY = 30;
      this.drawText('Resumen conciliaciones', 10, resumenTitleY, {
        size: 12,
        bold: true,
      });
      this.addResumeTable(resumenTitleY + 6);
    }
  }

  private addDocumentsTable(startY: number): number {
    const executedDocuments = this.documents.filter((document) => {
      return (
        document.payInConciliationId &&
        document.payInConciliationId > 0 &&
        document.amountConciliated &&
        document.amountConciliated > 0
      );
    });
    const sortedDocuments = executedDocuments.sort((a, b) => {
      const productOrder: Record<string, number> = {
        factoring: 1,
        credit: 2,
        invoice: 3,
        confirming: 4,
      };
      const aOrder = productOrder[String(a.productType)] || 5;
      const bOrder = productOrder[String(b.productType)] || 5;
      if (aOrder !== bOrder) return aOrder - bOrder;
      return a.folio.localeCompare(b.folio);
    });

    // Definir columnas con sus condiciones de visibilidad
    const columnDefinitions: Array<{
      header: string;
      field: keyof PayInExportConfig;
      getValue: (doc: DocumentWithConciliation) => string;
    }> = [
      {
        header: 'Folio',
        field: 'showFolio',
        getValue: (doc) => doc.folio,
      },
      {
        header: 'Tipo',
        field: 'showType',
        getValue: (doc) => getProductTypeLabel(doc.productType),
      },
      {
        header: 'Cliente',
        field: 'showClient',
        getValue: (doc) =>
          `${doc.businessIdentifier}\n${getShortString(doc.businessName)}`,
      },
      {
        header: 'Deudor',
        field: 'showDebtor',
        getValue: (doc) =>
          `${doc.stakeholderIdentifier}\n${getShortString(doc.stakeholderName)}`,
      },
      {
        header: 'Pagado por',
        field: 'showPaidBy',
        getValue: (doc) => doc.paidBy,
      },
      {
        header: 'Monto doc.',
        field: 'showAmount',
        getValue: (doc) => `${formatMoney(doc.amount)}`,
      },
      {
        header: 'Saldo',
        field: 'showBalance',
        getValue: (doc) =>
          `Doc.: ${formatMoney(doc.documentAmountDebtBefore ?? 0)}\nMora: ${formatMoney(doc.arrearAmountDebtBefore ?? 0)}\nTotal: ${formatMoney((doc.documentAmountDebtBefore ?? 0) + (doc.arrearAmountDebtBefore ?? 0))}`,
      },
      {
        header: 'Conciliado',
        field: 'showConciliated',
        getValue: (doc) =>
          `Doc.: ${formatMoney(doc.documentAmountConciliated ?? 0)}\nMora: ${formatMoney(doc.arrearAmountConciliated ?? 0)}\nTotal: ${formatMoney((doc.documentAmountConciliated ?? 0) + (doc.arrearAmountConciliated ?? 0))}`,
      },
      {
        header: 'Restante',
        field: 'showRemaining',
        getValue: (doc) =>
          `Doc.: ${formatMoney(doc.documentAmountDebtAfter ?? 0)}\nMora: ${formatMoney(doc.arrearAmountDebtAfter ?? 0)}\nTotal: ${formatMoney((doc.documentAmountDebtAfter ?? 0) + (doc.arrearAmountDebtAfter ?? 0))}`,
      },
      {
        header: 'Mora',
        field: 'showArrearRate',
        getValue: (doc) => {
          const arrearRateFormatted = doc.arrearRate
            ? `${doc.arrearRate}%`
            : '0%';
          return `${arrearRateFormatted}\nConciliado:\n${formatMoney(doc.arrearAmountConciliated ?? 0)}`;
        },
      },
    ];

    // Filtrar columnas visibles
    const visibleColumns = columnDefinitions.filter((col) =>
      this.shouldShowField(col.field),
    );

    // Construir headers y body dinámicamente
    const headers = [visibleColumns.map((col) => col.header)];
    const body = sortedDocuments.map((document) =>
      visibleColumns.map((col) => col.getValue(document)),
    );

    autoTable(this.doc, {
      startY,
      head: headers,
      body: body,
      theme: 'grid',
      headStyles: {
        fillColor: '#efefef',
        textColor: '#1b1b1b',
        fontStyle: 'bold',
        halign: 'left',
        valign: 'middle',
        lineColor: [44, 62, 80],
        fontSize: 9,
      },
      bodyStyles: {
        textColor: [50, 50, 50],
        fontSize: 8,
      },
      styles: {
        font: 'helvetica',
        lineColor: [44, 62, 80],
        lineWidth: 0.1,
      },
    });
    return (this.doc as any).lastAutoTable
      ? (this.doc as any).lastAutoTable.finalY
      : startY + 40;
  }

  private addFncTable(startY: number): number {
    autoTable(this.doc, {
      startY,
      head: [['Id', 'Monto', 'Fecha conciliación FNC', 'Monto conciliado FNC']],
      body: this.fncConciliations.map((fnc) => [
        fnc.id,
        formatMoney(fnc.amount),
        formatDate(new Date(fnc.createdAt), 'dd-mm-yyyy'),
        formatMoney(fnc.amount),
      ]),
      theme: 'grid',
      headStyles: {
        fillColor: '#efefef',
        textColor: '#1b1b1b',
        fontStyle: 'bold',
        halign: 'left',
        valign: 'middle',
        lineColor: [44, 62, 80],
        fontSize: 9,
      },
      bodyStyles: {
        textColor: [50, 50, 50],
        fontSize: 8,
      },
      styles: {
        font: 'helvetica',
        lineColor: [44, 62, 80],
        lineWidth: 0.1,
      },
    });
    return (this.doc as any).lastAutoTable
      ? (this.doc as any).lastAutoTable.finalY
      : startY + 40;
  }

  private addResumeTable(startY: number): void {
    const executedDocuments = this.documents.filter((document) => {
      return (
        document.payInConciliationId &&
        document.payInConciliationId > 0 &&
        document.amountConciliated &&
        document.amountConciliated > 0
      );
    });
    const totalDocumentAmountDebtBefore = reduceDocumentsByProperty(
      executedDocuments,
      'documentAmountDebtBefore',
      'highest',
    );
    const totalArrearAmountDebtBefore = reduceDocumentsByProperty(
      executedDocuments,
      'arrearAmountDebtBefore',
      'highest',
    );
    const totalDocumentAmountConciliated = reduceDocumentsByProperty(
      executedDocuments,
      'documentAmountConciliated',
      'all',
    );
    const totalArrearAmountConciliated = reduceDocumentsByProperty(
      executedDocuments,
      'arrearAmountConciliated',
      'all',
    );
    const totalDocumentAmountDebtAfter = reduceDocumentsByProperty(
      executedDocuments,
      'documentAmountDebtAfter',
      'lowest',
    );
    const totalArrearAmountDebtAfter = reduceDocumentsByProperty(
      executedDocuments,
      'arrearAmountDebtAfter',
      'lowest',
    );
    const totalFncAmount = this.fncConciliations.reduce(
      (acc, fnc) => acc + fnc.amount,
      0,
    );
    const data = [
      [
        'Documento',
        `${formatMoney(totalDocumentAmountDebtBefore)}`,
        `${formatMoney(totalDocumentAmountConciliated)}`,
        `${formatMoney(totalDocumentAmountDebtAfter)}`,
      ],
      [
        'Mora',
        `${formatMoney(totalArrearAmountDebtBefore)}`,
        `${formatMoney(totalArrearAmountConciliated)}`,
        `${formatMoney(totalArrearAmountDebtAfter)}`,
      ],
      this.fncConciliations.length > 0
        ? [
            'Subtotal documentos',
            `${formatMoney(totalDocumentAmountDebtBefore + totalArrearAmountDebtBefore)}`,
            `${formatMoney(totalDocumentAmountConciliated + totalArrearAmountConciliated)}`,
            `${formatMoney(totalDocumentAmountDebtAfter + totalArrearAmountDebtAfter)}`,
          ]
        : null,
      this.fncConciliations.length > 0
        ? [
            'FNC',
            `${formatMoney(totalFncAmount)}`,
            `${formatMoney(totalFncAmount)}`,
            `${formatMoney(0)}`,
          ]
        : null,
      [
        'Total',
        `${formatMoney(totalDocumentAmountDebtBefore + totalArrearAmountDebtBefore + totalFncAmount)}`,
        `${formatMoney(totalDocumentAmountConciliated + totalArrearAmountConciliated + totalFncAmount)}`,
        `${formatMoney(totalDocumentAmountDebtAfter + totalArrearAmountDebtAfter)}`,
      ],
    ].filter(Boolean) as string[][];
    const headers = ['', 'Saldo', 'Conciliado', 'Restante'];
    autoTable(this.doc, {
      startY,
      head: [headers],
      body: data,
      theme: 'grid',
      headStyles: {
        fillColor: '#efefef',
        textColor: '#1b1b1b',
        fontStyle: 'bold',
        halign: 'left',
        valign: 'middle',
        lineColor: [44, 62, 80],
        fontSize: 9,
      },
      bodyStyles: {
        textColor: [50, 50, 50],
        fontSize: 8,
      },
      styles: {
        font: 'helvetica',
        lineColor: [44, 62, 80],
        lineWidth: 0.1,
      },
      didParseCell: (d: any) => {
        const hasFnc = this.fncConciliations.length > 0;
        const subtotalRowIndex = hasFnc ? 2 : -1;
        const totalRowIndex = data.length - 1;
        if (d.row.index === subtotalRowIndex || d.row.index === totalRowIndex) {
          d.cell.styles.fontStyle = 'bold';
        }
      },
    });
  }
}
